import { useEffect, useState } from "react";
import { SpaceBetween } from "@cloudscape-design/components";

import { NewAccountPageTrigger } from './PageTrigger'
import { useParams } from "react-router";
import { PagesEnum } from "./PageTrigger";
import { ENDPOINT, RegistrationData } from "../../../../data/FormData";
import { support_contact } from "../../../../res/support"
import { renderProviderLogo } from '../../../../components/ProviderLogo';
import { renderPowerBy } from "../../../../components/PowerBy";

// JSX element for support message
const support: JSX.Element = support_contact

/**
 * QuestionnairePagesContainer:
 * Main container responsible for displaying onboarding questionnaire pages.
 * It validates the registration token, loads provider and user data, and renders the appropriate page.
 */
export function QuestionnairePagesContainer(): JSX.Element {
    // Read the "token" from the URL (e.g. /register/:token)
    const { token } = useParams()

    // Tracks which questionnaire page is currently active
    const [showPage, setShowPage] = useState<PagesEnum>(PagesEnum.First);

    // The actual content (JSX) for the active page, loaded dynamically
    const [showPageContent, setShowPageContent] = useState<JSX.Element>(<div />);

    useEffect(() => {
        // When the component mounts or when `token`/`showPage` changes, validate the token
        fetch(`${ENDPOINT}/validregistrationtoken?token=${token}`)
            .then(response => {
                if (response.status === 200) {
                    return response.json();
                } else {
                    // If token is invalid, show error page with support contact info
                    const invalidPage: JSX.Element = <div style={{ marginTop: '30px' }}>
                        <p>This is not a valid registration link. </p>
                        {support}
                    </div>
                    setShowPageContent(invalidPage)
                    return
                }
            }).then(data => {
                if (!data) return;

                // Store user and provider information in the shared registration singleton
                RegistrationData.instance.pid = data["pid"]
                RegistrationData.instance.cell = data["cell"]
                RegistrationData.instance.email = data["email"]
                RegistrationData.instance.token = token!

                // Provider-related metadata
                RegistrationData.instance.providerID = data["provider"]["provider"]
                RegistrationData.instance.providerName = data["provider"]["name"]
                RegistrationData.instance.providerSupportEmail = data["provider"]["support_email"]
                RegistrationData.instance.providerSupportCell = data["provider"]["support_cell"]
                RegistrationData.instance.providerCountry = data["provider"]["country"]

                // Display conditions for medication questions
                RegistrationData.instance.show_htn_med_question = data["show_htn_med_question"]
                RegistrationData.instance.show_dm_med_question = data["show_dm_med_question"]
                RegistrationData.instance.show_insulin_question = data["show_insulin_question"]

                // Wearable device configuration
                RegistrationData.instance.show_wearable_device_question = data["show_wearable_device_question"]
                RegistrationData.instance.provide_device_option = data["provide_device_option"]
                RegistrationData.instance.opt_out_wearable_option = data["opt_out_wearable_option"]

                // Set up the page switching function (used by inner components)
                NewAccountPageTrigger.instance.setNewAccountShowPage = (page: PagesEnum): void => {
                    setShowPage(page)
                }

                // Get the current page content to show
                setShowPageContent(NewAccountPageTrigger.instance.getShowPage(showPage))
            })
            .catch(err => {
                console.log(err)
            })

    }, [token, showPage])

    return (
        <div className="App center-screen">
            <div className="glow-border page-container" style={{ width: "330px" }}>
                <SpaceBetween size='xs' direction='vertical'>
                    {/* Optional logo from provider */}
                    {RegistrationData.instance.providerID ? renderProviderLogo({ provider: RegistrationData.instance.providerID.toUpperCase() }) : null}

                    {/* Title */}
                    <div>
                        <h3 className='line-align-center' style={{ marginBottom: '0' }}>Tailored Care and Prevention of Chronic Conditions</h3>
                    </div>

                    {/* Main Content: Current page of questionnaire */}
                    {showPageContent}

                    {/* Branding footer */}
                    {RegistrationData.instance.providerID ? renderPowerBy({ provider: RegistrationData.instance.providerID.toUpperCase() }) : null}
                </SpaceBetween>
            </div>
        </div>
    );
}
export default QuestionnairePagesContainer